import React from 'react';
import './EventProcessStepper.css';
import { currentStepKey, stepIcon, STEP_STATUS_LABEL } from '../process/processUtils';

// Process stepper in the same DHL style as EventStatus, but as a primary, large component with
// labels. The step list is dynamic (copied from the process template, editable per process); every
// step brings its own icon (fallback per kind) and status. The synthetic final step
// ("Fertig") is always the last node.
// Status per step (from the backend):
//   "done"    → executed (green)
//   "skipped" → deliberately skipped (grey filled)
//   "pending" → still open (outline)
//   "closed"  → process completed (final node, green)
// Navigation works on step keys only (never on numbers ±1):
// currentKey = first pending step (pulse ring); computed from the steps when not passed.
// viewedKey  = the step shown below (pure navigation, no status change). If it differs from
//   currentKey, the node gets an accent ring.
// When interactive and onStepNavigate is set, ALL nodes are clickable → onStepNavigate(step.key).
function EventProcessStepper({ steps = [], currentKey, viewedKey, onStepNavigate, interactive = true, closed = false }) {
  const navigable = interactive && typeof onStepNavigate === 'function';
  const current = currentKey ?? currentStepKey({ steps, closed });

  return (
    <div className="processStepperTrack" role="list" aria-label="Prozess-Fortschritt">
      {steps.map((step, idx) => {
        const isLast = idx === steps.length - 1;
        const isCurrent = step.key === current;
        // Highlight the viewed step only when it differs from the current one (otherwise the accent
        // ring would cover the pulse of the current step).
        const isViewed = step.key === viewedKey && !isCurrent;
        const statusText = STEP_STATUS_LABEL[step.status] ?? '';
        const label = statusText ? `${step.label} – ${statusText}` : step.label;

        // Connector progress: LEFT (this) step done/skipped → partly green (progress flows to the
        // next step). RIGHT (next) step reached too → fully green. Otherwise neutral.
        const nextStep = steps[idx + 1];
        const isReached = (s) => !!s && (s.status === 'done' || s.status === 'skipped' || s.status === 'closed');
        const connectorClass = isReached(nextStep) ? 'isReached' : (isReached(step) ? 'isPartial' : '');

        const nodeClass = [
          'processStepNode',
          `is-${step.status}`,
          isCurrent ? 'isCurrent' : '',
          isViewed ? 'isViewed' : '',
          navigable ? 'isClickable' : '',
        ].filter(Boolean).join(' ');

        return (
          <React.Fragment key={step.key}>
            <div className="processStepItem" role="listitem">
              <button
                type="button"
                className={nodeClass}
                onClick={navigable ? () => onStepNavigate(step.key) : undefined}
                disabled={!navigable}
                aria-label={label}
                aria-current={isViewed || isCurrent ? 'step' : undefined}
                title={navigable ? `${label} – Schritt öffnen` : label}
              >
                <span className="material-symbols-rounded processStepIcon" aria-hidden="true">{stepIcon(step)}</span>
              </button>
              <span className={`processStepLabel ${isCurrent ? 'isCurrent' : ''}`}>{step.label}</span>
            </div>
            {!isLast && <span className={`processStepConnector ${connectorClass}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default EventProcessStepper;
